import { useState, useEffect, useCallback, useMemo } from 'react';
import { ref, onValue, push, update, remove } from 'firebase/database';
import { auth, database } from '../firebase';

/**
 * Hook for managing savings goals.
 * Reads from flo/savingsGoals in Firebase.
 */
const useSavingsData = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        setGoals([]);
        setLoading(false);
        return;
      }

      const goalsRef = ref(database, `users/${user.uid}/flo/savingsGoals`);
      const unsub = onValue(goalsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const parsed = Object.entries(data).map(([id, val]) => ({ id, ...val }));
          parsed.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
          setGoals(parsed);
        } else {
          setGoals([]);
        }
        setLoading(false);
      });

      return () => unsub();
    });

    return () => unsubscribe();
  }, []);

  const addGoal = useCallback(async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const now = new Date().toISOString();
    const goalsRef = ref(database, `users/${user.uid}/flo/savingsGoals`);
    await push(goalsRef, { ...data, createdAt: now, updatedAt: now });
  }, []);

  const updateGoal = useCallback(async (id, updates) => {
    const user = auth.currentUser;
    if (!user) return;
    const goalRef = ref(database, `users/${user.uid}/flo/savingsGoals/${id}`);
    await update(goalRef, { ...updates, updatedAt: new Date().toISOString() });
  }, []);

  const deleteGoal = useCallback(async (id) => {
    const user = auth.currentUser;
    if (!user) return;
    await remove(ref(database, `users/${user.uid}/flo/savingsGoals/${id}`));
  }, []);

  // Derived
  const totalSaved = useMemo(
    () => goals.reduce((sum, g) => sum + (g.currentAmount || 0), 0),
    [goals]
  );

  const totalTarget = useMemo(
    () => goals.reduce((sum, g) => sum + (g.targetAmount || 0), 0),
    [goals]
  );

  return {
    goals,
    loading,
    addGoal,
    updateGoal,
    deleteGoal,
    totalSaved,
    totalTarget,
  };
};

export default useSavingsData;
